import type { CardId, WallCard, WallShape } from '@walls/store';
import type { SortedBoardZIndexes } from './types';

const promotedZIndex = 999999999999999;
export const drawingLayerZIndex = 1;
export const connectionLayerZIndex = 2;
export const cardLayerBaseZIndex = connectionLayerZIndex + 1;

export function sortBoardZIndexes(
  cards: WallCard[],
  shapes: WallShape[],
  promotedElementIds?: string | string[] | null
): SortedBoardZIndexes {
  const promotedElementIdSet = new Set(
    Array.isArray(promotedElementIds)
      ? promotedElementIds
      : promotedElementIds
        ? [promotedElementIds]
        : []
  );
  const topLevelCards = flattenTopLevelCards(cards);
  const lockedShapes = sortByZ(shapes.filter((shape) => shape.isLocked), promotedElementIdSet);
  const unlockedShapes = sortByZ(shapes.filter((shape) => !shape.isLocked), promotedElementIdSet);
  const lockedCards = sortByZ(topLevelCards.filter((card) => card.isLocked), promotedElementIdSet);
  const unlockedCards = sortByZ(topLevelCards.filter((card) => !card.isLocked), promotedElementIdSet);
  const sortedCards: Array<{ cardId: CardId; zPos: number }> = [];
  const sortedShapes: Array<{ shapeId: string; zPos: number }> = [];

  lockedShapes.forEach((shape, index) => {
    sortedShapes.push({ shapeId: shape.id, zPos: index });
  });

  unlockedShapes.forEach((shape, index) => {
    sortedShapes.push({ shapeId: shape.id, zPos: lockedShapes.length + index });
  });

  lockedCards.forEach((card, index) => {
    sortedCards.push({ cardId: card.id, zPos: cardLayerBaseZIndex + index });
  });

  unlockedCards.forEach((card, index) => {
    sortedCards.push({
      cardId: card.id,
      zPos: cardLayerBaseZIndex + lockedCards.length + index,
    });
  });

  return {
    canvas: {
      drawingCanvasZIndex: drawingLayerZIndex,
      lockedDrawingCanvasZIndex: drawingLayerZIndex,
    },
    cards: sortedCards,
    shapes: sortedShapes,
  };
}

function flattenTopLevelCards(cards: WallCard[]) {
  return cards.filter((card) => !card.parentId);
}

function sortByZ<T extends { id: string; zPos?: number }>(
  items: T[],
  promotedElementIds: Set<string>
) {
  return [...items].sort(
    (a, b) => getSortZ(a, promotedElementIds) - getSortZ(b, promotedElementIds)
  );
}

function getSortZ(item: { id: string; zPos?: number }, promotedElementIds: Set<string>) {
  const zPos = item.zPos ?? 0;

  return promotedElementIds.has(item.id) ? promotedZIndex + zPos : zPos;
}
